<template lang="pug">
  ws-dialog(v-model="dialog" :title="$t('topic.form:heading')")
    template(v-if="!isSpam")
      validation-observer(v-slot="{ handleSubmit, invalid }" ref="form")
        v-form( @submit.prevent="handleSubmit(saveTopic)")
          v-card-text
            v-select.mb-3(outlined v-model="category" :menu-props="{maxHeight:800}" :label="$t('topic.form:category')" :items="items" hide-details="auto")
            v-autocomplete.mb-3(v-if="showDestinationPicker" return-object v-model="destinationSelection" :menu-props="{maxHeight:800}" item-text="name" item-value="id" hide-details="auto" :loading="destinationLoading" :items="destinationItems" :search-input.sync="destinationSearch" :no-data-text="$t('topic.form:destination.no.data')" :placeholder="$t('topic.form:destination.placeholder')" :label="$t('topic.form:destination')" outlined)
              template(#item="data")
                v-list-item-avatar(size="20")
                  ws-icon(small) {{icons[data.item.__typename]}}
                v-list-item-content {{data.item.name}}
            validation-provider(v-slot="{errors, touched}" rules="required" :name="$t('topic.form:subject')" slim)
              v-text-field(outlined v-model="subject" :label="$t('topic.form:subject')" :error-messages="touched ? errors : []")
            h3.mt-3 {{$t('topic.form:content')}}
            validation-provider(v-slot="{errors}" rules="wscontent" name="content" slim :customMessages="{wscontent: $t('reply-form:reply.error.required')}")
              v-input(:error-messages="errors")
                forum-content-editor(v-model="content" type="textarea")
            v-checkbox(v-model="notifyMe" :label="$t('page.forum.new:subscribe.to.topic')")
          v-card-actions
            v-spacer
            v-btn(outlined type="submit" color="primary" :disabled="invalid") {{$t('topic.form:topic_submit')}}
    template(v-else)
      v-alert.mb-0(type="error" color="error" text border="bottom")
        strong {{$t('topic:marked_as_spam.title')}}
        div {{$t('topic:marked_as_spam.content')}}

</template>

<script>
import gql from 'graphql-tag';
import ForumContentEditor from '@/components/forum/forum-content-editor';

import Searchbar from '@/components/searchbar.vue';

export default {
	components: {
		ForumContentEditor,
		Searchbar
	},
	props: {
		value: {
			type: Boolean,
			default: false
		},
		initialCategory: {
			type: String,
			default: null
		},
		initialDestination: {
			type: Object,
			required: false,
			default: () => {
				return null;
			}
		}
	},
	data() {
		return {
			dialog: false,
			isSpam: false,
			subject: null,
			content: { markdown: '' },
			category: 'default',
			destinationSearch: '',
			destinationLoading: false,
			destinationItems: [],
			destinationSelection: {},
			notifyMe: false,
			destinationableCategories: [
				'travelogues',
				'destination',
				'work',
				'travel'
			],
			icons: {
				Area: 'ski',
				Resort: 'village',
				Cluster: 'ski',
				Organisation: 'organisation',
				Skihall: 'skihall',
				Revolvingslope: 'revolvingslope',
				Dryslope: 'dryslope',
				Carpetslope: 'carpetslope'
			},
			items: [
				{ value: 'default', text: this.$t('forum-navigation:link.default') },
				{
					value: 'weather_snow',
					text: this.$t('forum-navigation:link.weather_snow')
				},
				{ value: 'destination', text: this.$t('forum-navigation:link.areas') },
				{
					value: 'travelogues',
					text: this.$t('forum-navigation:link.travelogues')
				},
				{ value: 'gear', text: this.$t('forum-navigation:link.gear') },
				{ value: 'travel', text: this.$t('forum-navigation:link.travel') },
				{
					value: 'marketplace',
					text: this.$t('forum-navigation:link.marketplace')
				},
				{
					value: 'photo_video',
					text: this.$t('forum-navigation:link.photo_video')
				},
				{
					value: 'meet_greet',
					text: this.$t('forum-navigation:link.meet_greet')
				},
				{ value: 'work', text: this.$t('forum-navigation:link.work') },
				{ value: 'stube', text: this.$t('forum-navigation:link.stube') }
			]
		};
	},
	computed: {
		showDestinationPicker() {
			return this.destinationableCategories.includes(this.category);
		},
		topicDestination() {
			if (!this.destinationSelection) return false;

			return {
				type: this.destinationSelection.__typename,
				id: this.destinationSelection.id
			};
		}
	},
	watch: {
		value(value, _oldValue) {
			this.dialog = value;
		},
		dialog(value, _oldValue) {
			this.$emit('input', value);
		},
		destinationSearch(value, _oldValue) {
			this.searchDestinations(value);
		}
	},
	created() {
		if (this.initialCategory) {
			this.category = this.initialCategory;
		}
		if (this.initialDestination) {
			this.destinationItems.push(this.initialDestination);
			this.destinationSelection = this.initialDestination;
		}
	},
	methods: {
		saveTopic() {
			const topicContent = this.$lodash.cloneDeep(this.content);

			const topic = {
				title: this.subject,
				content: topicContent,
				category: this.category,
				notifyMe: this.notifyMe,
				destination: this.topicDestination
			};

			this.$graphClient
				.mutate({
					mutation: gql`
						mutation($topic: TopicInput) {
							saveTopic(topic: $topic) {
								id
								slug
								isSpam
							}
						}
					`,
					errorPolicy: 'all',
					variables: {
						topic
					}
				})
				.then(({ data, errors }) => {
					if (errors) {
						if (errors[0].message.includes('spamError:')) {
							this.isSpam = true;
						} else {
							throw errors[0];
						}
					}

					if (data.saveTopic.id) {
						if (!data.saveTopic.isSpam) {
							this.$router.push(
								this.localePath({
									name: 'forum-topic',
									params: { slug: parseInt(data.saveTopic.slug) }
								})
							);
						} else {
							this.isSpam = true;
						}
					}
				})
				.catch(_error => {
					this.$toast.error(this.$t('topic.form:submit.error'));
				});
		},
		searchDestinations(val) {
			if (val == null || val === '') return false;
			this.isLoading = true;

			const searchQuery = gql`
				query search($val: String!, $types: [String]!, $limit: Int!) {
					search(term: $val, types: $types, limit: $limit) {
						__typename
						... on Area {
							id
							name
							slug
						}
						... on Resort {
							id
							name
							slug
						}
						... on Cluster {
							id
							name
							slug
						}
						... on Skihall {
							id
							name
							slug
						}
					}
				}
			`;

			this.$graphClient
				.query({
					query: searchQuery,
					variables: {
						val,
						types: ['areas', 'resorts', 'clusters', 'skihalls'],
						limit: 8
					}
				})
				.then(({ data }) => {
					this.destinationItems = data.search;
				})
				.catch(_err => {})
				.finally(() => (this.isLoading = false));
		}
	}
};
</script>
